import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { Download, Plus, Upload } from 'lucide-react';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { useMediaManagerTemplateGroupQuery, useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import ImportConflictsModal from 'modules/manager/ui/components/import-conflicts-modal';
import ImportTemplatesResultModal from 'modules/manager/ui/components/import-templates-result-modal';
import MediaManagerPresetCard from 'modules/manager/ui/components/media-manager-preset-card';
import MediaManagerTemplateList from 'modules/manager/ui/components/media-manager-template-list';
import { PRESET_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { PresetGridSkeleton } from 'modules/manager/ui/components/skeletons';
import useCreateTemplateModal from 'modules/manager/ui/hooks/modals/use-create-template-modal';
import useDeleteTemplateModal from 'modules/manager/ui/hooks/modals/use-delete-template-modal';
import useDuplicateTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-duplicate-template-group-modal';
import useDuplicateTemplateModal from 'modules/manager/ui/hooks/modals/use-duplicate-template-modal';
import useEditTemplateModal from 'modules/manager/ui/hooks/modals/use-edit-template-modal';
import useMoveTemplateModal from 'modules/manager/ui/hooks/modals/use-move-template-modal';
import { useCreateTemplateQueryParam } from 'modules/manager/ui/hooks/use-create-template-query-param';
import { useExportTemplates } from 'modules/manager/ui/hooks/use-export-templates';
import { useImportTemplates } from 'modules/manager/ui/hooks/use-import-templates';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { deriveAsyncState } from 'shared/utils/async-state';
import { UDButton, UDText } from 'ud-ui/components';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';

const HIGHLIGHT_DURATION_MS = 4000;

function parseId(value: string | undefined): number | null {
  if (!value) return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function groupScope(group: MediaManagerTemplateGroup | undefined): 'unknown' | 'org' | 'preset' {
  if (group == null) return 'unknown';
  return group.tournamentId == null ? 'preset' : 'org';
}

const MediaManagerTemplateGroupPage: React.FC = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const { templateGroupId: templateGroupIdParam } = useParams<{ templateGroupId: string }>();
  const templateGroupId = parseId(templateGroupIdParam);

  const {
    data: group,
    currentData,
    isFetching,
    isError,
    refetch,
  } = useMediaManagerTemplateGroupQuery(templateGroupId ?? 0, { skip: templateGroupId == null });

  const templates = group?.templates ?? [];
  const scope = groupScope(group);
  const isPresetGroup = scope === 'preset';
  const canWrite = scope === 'preset' ? isAdmin : scope === 'org';
  const listState = deriveAsyncState({
    itemsCount: templates.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });

  const { data: orgGroups = [] } = useMediaManagerTemplateGroupsQuery({ tournamentId: tournamentId ?? 0 }, { skip: !tournamentId });

  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  useEffect(() => {
    const fromNav = (location.state as { highlightTemplateId?: number } | null)?.highlightTemplateId;
    if (fromNav != null) setHighlightedId(fromNav);
  }, [location.state]);

  useEffect(() => {
    if (highlightedId == null) return;
    const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [highlightedId]);

  const breadcrumbs = useMemo(() => {
    const trail = [{ label: t`Медиа менеджер`, to: Pages.MEDIA_MANAGER.INDEX }];
    if (isPresetGroup) {
      trail.push({ label: t`Общие шаблоны`, to: Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY });
    }
    trail.push({
      label: group?.name ?? t`Группа`,
      to: templateGroupId ? Pages.MEDIA_MANAGER.builders.group(templateGroupId) : Pages.MEDIA_MANAGER.INDEX,
    });
    return trail;
  }, [t, isPresetGroup, group?.name, templateGroupId]);

  useEffect(() => {
    setHeaderContent(<UDBreadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  const loadGroup = useCallback(() => {
    void refetch();
  }, [refetch]);

  const openTemplate = useCallback(
    (template: MediaManagerTemplate) => navigate(Pages.MEDIA_MANAGER.builders.editor(template.id)),
    [navigate],
  );

  const existingNames = useMemo(() => templates.map((item) => item.name.trim()), [templates]);
  const orgGroupNames = useMemo(() => orgGroups.map((item) => item.name.trim()), [orgGroups]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateModal({
    templateGroupId: templateGroupId ?? 0,
    existingNames,
    templatesCount: templates.length,
    title: isPresetGroup ? t`Создать пресет` : undefined,
  });
  // A click handler would pass its event as the initial values.
  const openBlankCreateModal = useCallback(() => openCreateModal(), [openCreateModal]);
  const openCreateModalForKind = useCallback(
    (mediaKind: MediaKind) => openCreateModal({ mediaKind, isDefaultForKind: true }),
    [openCreateModal],
  );
  // «Создать шаблон» from a championship's «Медиа» tab arrives as `?create=<kind>` (IMGM-1518).
  useCreateTemplateQueryParam({ isReady: scope !== 'unknown', canWrite, onCreate: openCreateModalForKind });
  // The group refetches by the mutations' tags — the modals take no «reload» callback (IMGM-1515).
  const { modal: editModal, open: openEditModal } = useEditTemplateModal({ templates });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateModal();
  const { modal: moveModal, open: openMoveModal } = useMoveTemplateModal({ groups: orgGroups });
  const {
    modal: duplicateModal,
    open: openDuplicateModal,
    canDuplicate,
    isDuplicating,
  } = useDuplicateTemplateModal({
    onDuplicated: (copy) => {
      navigate(Pages.MEDIA_MANAGER.builders.group(copy.templateGroupId), { state: { highlightTemplateId: copy.id } });
    },
  });
  const { modal: duplicateGroupModal, open: openDuplicateGroupModal } = useDuplicateTemplateGroupModal({
    existingNames: orgGroupNames,
    onDuplicated: (copy) => navigate(Pages.MEDIA_MANAGER.builders.group(copy.id)),
  });

  const { exportTemplates, isExporting } = useExportTemplates();
  const runExport = useCallback(
    (list: readonly MediaManagerTemplate[]) => {
      exportTemplates(list, { label: group?.name }).catch((error) => {
        console.error('Failed to export media manager templates:', error);
        toast.error(t`Не удалось выгрузить шаблоны, попробуйте ещё раз`);
        loadGroup();
      });
    },
    [exportTemplates, group?.name, loadGroup, t],
  );
  const exportOne = useCallback((template: MediaManagerTemplate) => runExport([template]), [runExport]);

  const importTarget = useMemo(
    () => (templateGroupId && canWrite ? { templateGroupId, isPreset: isPresetGroup } : null),
    [templateGroupId, canWrite, isPresetGroup],
  );
  const {
    pickFile,
    fileInput,
    isImporting,
    result: importResult,
    clearResult: clearImportResult,
    pending: pendingImport,
    confirmOverwrite,
    cancelImport,
  } = useImportTemplates({ target: importTarget, existing: templates, templatesCount: templates.length });

  useEffect(() => {
    if (importResult?.status !== 'done') return;
    const touched = [...importResult.created, ...importResult.overwritten];
    if (touched.length === 1) setHighlightedId(touched[0].id);
  }, [importResult]);

  if (templateGroupId == null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6" role="alert">
        <UDText variant="body-2">
          <Trans>Группа не найдена</Trans>
        </UDText>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex justify-end gap-3">
        {isPresetGroup && group && (
          <UDButton variant="secondary" className="flex items-center" onClick={() => openDuplicateGroupModal(group)}>
            <Trans>Дублировать группу</Trans>
          </UDButton>
        )}
        {canWrite && (
          <UDButton
            variant="secondary"
            className="flex items-center"
            disabled={!importTarget || isImporting}
            loading={isImporting}
            onClick={pickFile}
          >
            <Upload size={16} className="mr-2" />
            <Trans>Импорт из файла</Trans>
          </UDButton>
        )}
        <UDButton
          variant="secondary"
          className="flex items-center"
          disabled={templates.length === 0 || isExporting}
          loading={isExporting}
          onClick={() => runExport(templates)}
        >
          <Download size={16} className="mr-2" />
          <Trans>Экспорт всех</Trans>
        </UDButton>
        {canWrite && (
          <UDButton variant="dark" className="flex items-center" onClick={openBlankCreateModal}>
            <Plus size={16} className="mr-2" />
            <Trans>Добавить шаблон</Trans>
          </UDButton>
        )}
      </div>

      {isPresetGroup ? (
        listState.status === 'loading' ? (
          <PresetGridSkeleton isAdmin={isAdmin} canDuplicate={canDuplicate} />
        ) : listState.status === 'error' ? (
          <div className="flex h-full flex-col items-center justify-center gap-2" role="alert">
            <UDText variant="body-2">
              <Trans>Не удалось загрузить шаблоны</Trans>
            </UDText>
            <UDButton variant="secondary" onClick={loadGroup}>
              <Trans>Повторить</Trans>
            </UDButton>
          </div>
        ) : listState.status === 'empty' ? (
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <UDText variant="body-2" className="text-gray-500">
              <Trans>В группе пока нет шаблонов</Trans>
            </UDText>
            {canWrite && (
              <UDButton variant="primary" onClick={openBlankCreateModal}>
                <Plus size={16} className="mr-2" /> <Trans>Добавить шаблон</Trans>
              </UDButton>
            )}
          </div>
        ) : (
          <div
            className={classNames(PRESET_GRID_CLASS, 'transition-opacity', {
              'pointer-events-none opacity-50': listState.isRefreshing,
            })}
            aria-busy={listState.isRefreshing}
          >
            {templates.map((template) => (
              <MediaManagerPresetCard
                key={template.id}
                template={template}
                isAdmin={isAdmin}
                canDuplicate={canDuplicate}
                isDuplicating={isDuplicating}
                onOpen={openTemplate}
                onDuplicate={openDuplicateModal}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
                onExport={exportOne}
                isExporting={isExporting}
              />
            ))}
          </div>
        )
      ) : (
        <MediaManagerTemplateList
          templates={templates}
          state={listState}
          highlightedTemplateId={highlightedId}
          onCreate={openBlankCreateModal}
          onRetry={loadGroup}
          onSelect={openTemplate}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
          onExport={exportOne}
          onMove={openMoveModal}
          isExporting={isExporting}
        />
      )}

      {createModal}
      {editModal}
      {deleteModal}
      {moveModal}
      {duplicateModal}
      {duplicateGroupModal}
      {fileInput}
      {pendingImport && <ImportConflictsModal pending={pendingImport} onConfirm={confirmOverwrite} onCancel={cancelImport} />}
      {importResult && <ImportTemplatesResultModal result={importResult} onClose={clearImportResult} />}
    </div>
  );
};

export default MediaManagerTemplateGroupPage;
